import { toast as sonnerToast } from 'sonner'

const variantStyles = {
  neutral: { bar: 'bg-transparent' },
  info: { bar: 'bg-[#3B82F6]' },
  warning: { bar: 'bg-[#F59E0B]' },
  success: { bar: 'bg-[#22C55E]' },
  danger: { bar: 'bg-[#EF4444]' },
}

const normalizeErrorMessage = (input) => {
  const fallback = 'Something went wrong. Please try again.'

  if (!input) return fallback

  const fromResponse =
    input?.response?.data?.message ||
    input?.response?.data?.error ||
    input?.response?.data?.errors?.[0]?.message ||
    input?.response?.data?.errors?.[0]?.error ||
    input?.response?.data?.error ||
    input?.response?.message ||
    input?.response?.statusText ||
    input?.data?.message ||
    input?.message
  if (typeof fromResponse === 'string' && fromResponse.trim()) {
    return fromResponse.trim()
  }
  if (typeof input?.status === 'number' && typeof input?.message === 'string') {
    const msg = input.message.trim()
    return msg || `Request failed (${input.status})`
  }
  if (typeof input === 'string') {
    const raw = input.trim()
    if (raw.startsWith('{') || raw.startsWith('[')) {
      try {
        const parsed = JSON.parse(raw)
        const parsedMsg =
          parsed?.message ||
          parsed?.error ||
          parsed?.errors?.[0]?.message ||
          parsed?.errors?.[0]?.error ||
          null
        if (typeof parsedMsg === 'string' && parsedMsg.trim()) {
          return parsedMsg.trim()
        }
      } catch (_) {}
      return fallback
    }
    const unquoted = raw.replace(/^"+|"+$/g, '').trim()
    return unquoted || fallback
  }
  return fallback
}

const ToastContent = ({
  title,
  variant = 'neutral',
}) => {
  const styles = variantStyles[variant] || variantStyles.neutral

  return (
    <div className="flex items-center gap-4 -m-2">
      <span className={`w-1 rounded-full self-stretch ${styles.bar}`} aria-hidden />
      <div className="flex-1 min-w-0">
        <p className="text-base font-medium text-[#111827] whitespace-break-spaces">{title}</p>
      </div>
    </div>
  )
}

const getSonnerType = (variant) => {
  switch (variant) {
    case 'success':
      return 'success'
    case 'danger':
      return 'error'
    case 'warning':
      return 'warning'
    case 'info':
      return 'info'
    default:
      return 'default'
  }
}

const showVariantToast = ({
  title,
  message,
  variant = 'neutral',
  actionText,
  onAction,
  autoClose,
}) => {
  const duration = autoClose ?? 3000
  const toastType = getSonnerType(variant)
  const toastOptions = {
    duration,
    icon: null, // Disable icon
  }
  
  if (actionText && onAction) {
    toastOptions.action = {
      label: actionText,
      onClick: onAction,
    }
  }

  // Use JSX for custom content with colored bar
  sonnerToast[toastType](<ToastContent title={title} variant={variant} />, toastOptions)
}

// Public helpers
export const showCustomToast = (message, options = {}) => {
  const {
    type = 'success',
    actionText = 'Action',
    onAction,
    title,
  } = options
  showVariantToast({
    title: title || message,
    message,
    variant:
      type === 'error'
        ? 'danger'
        : type === 'warning'
        ? 'warning'
        : type === 'info'
        ? 'info'
        : 'success',
    actionText,
    onAction,
    autoClose: 3000,
  })
}

export const showSuccessToast = (message, options = {}) => {
  showVariantToast({
    title: message,
    message,
    variant: 'success',
    actionText: options.actionText,
    onAction: options.onAction,
    autoClose: 3000,
  })
}

export const showErrorToast = (message, options = {}) => {
  showVariantToast({
    title: message,
    message,
    variant: 'danger',
    actionText: options.actionText,
    onAction: options.onAction,
    autoClose: 3000,
  })
}

export const showSimpleSuccess = (message) => {
  showVariantToast({
    title: message,
    message,
    variant: 'success',
    autoClose: 2500,
  })
}

export const showSimpleError = (message) => {
  const cleanMessage = normalizeErrorMessage(message)

  showVariantToast({
    title: cleanMessage,
    message: cleanMessage,
    variant: 'danger',
    autoClose: 3000,
  })
}

export const showSimpleWarning = (message) => {
  showVariantToast({
    title: message,
    message,
    variant: 'warning',
    autoClose: 2500,
  })
}